import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Download, Loader2, X, FileText, ShieldCheck, Trash2, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { downloadDocument, handlePdfError } from "../../lib/pdf";
import { useAuth } from "../../context/AuthContext";
import { creditLabel } from "../../lib/pricing";
import { useOnline } from "../../hooks/usePWA";
import { Button } from "../ui/button";
import ExpiryCountdown from "../ExpiryCountdown";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";

const PURGE_NOTICE =
  "For your security, files are automatically and permanently purged from our servers 24 hours after generation.";

// Watermarked preview -> unlock (spend credit + download clean file) -> optional instant server wipe.
export default function PreviewUnlock({ stage, onClose, onUnlocked }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloadedDoc, setDownloadedDoc] = useState(null);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();

  useEffect(() => {
    if (!stage?.prep_id || !stage?.has_preview) { setLoadingPreview(false); return; }
    let active = true;
    let url;
    api.get(`/preview/${stage.prep_id}`, { responseType: "blob" })
      .then((res) => { if (active) { url = URL.createObjectURL(res.data); setPreviewUrl(url); } })
      .catch(() => {})
      .finally(() => { if (active) setLoadingPreview(false); });
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [stage]);

  const unlock = async () => {
    setUnlocking(true);
    try {
      const res = await api.post("/unlock", { prep_id: stage.prep_id });
      await refreshUser();
      const d = res.data.document;
      toast.success("Unlocked & downloading", { description: `${creditLabel(stage.cost)} used.` });
      await downloadDocument(d.id, d.title, d.ext || "pdf");
      setDownloadedDoc(d);
    } catch (e) {
      if (handlePdfError(e) === "no-credits") navigate("/buy-credits");
    } finally {
      setUnlocking(false);
    }
  };

  const deleteNow = async () => {
    if (!downloadedDoc) return;
    setDeleting(true);
    try {
      await api.delete(`/documents/${downloadedDoc.id}`);
      toast.success("Permanently wiped from our servers", { description: "Your download stays safe on your device." });
      onUnlocked?.();
    } catch (e) {
      toast.error("Could not delete", { description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="preview-unlock-overlay" className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-5 w-5 text-indigo-600 shrink-0" />
            <p className="font-display font-semibold truncate" data-testid="preview-title">{stage?.title || "Preview"}</p>
            {stage?.pages_rendered != null && (
              <span
                data-testid="slides-rendered-badge"
                className="shrink-0 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full px-2 py-0.5"
              >
                {stage.pages_rendered} {stage.pages_rendered === 1 ? "slide" : "slides"} rendered
              </span>
            )}
          </div>
          <button data-testid="preview-unlock-close" onClick={onClose} className="text-slate-400 hover:text-slate-700 rounded-lg p-1">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-auto bg-slate-100 p-4 flex items-center justify-center min-h-[45vh]">
          {loadingPreview ? (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Rendering preview…</span>
            </div>
          ) : previewUrl ? (
            <iframe data-testid="preview-iframe" title="Document preview" src={previewUrl} className="w-full h-[60vh] bg-white rounded-lg border border-slate-200" />
          ) : (
            <div className="text-center text-slate-500 text-sm px-6">
              <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              Preview isn't available for this file, but it's ready to unlock and download.
            </div>
          )}
        </div>

        {!downloadedDoc ? (
          <footer className="px-5 py-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3">
            <p data-testid="purge-notice" className="text-xs text-slate-500 flex items-start gap-1.5 flex-1">
              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600 mt-px" />
              {PURGE_NOTICE}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid="preview-unlock-cancel" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800 px-3 py-2">
                Cancel
              </button>
              <Button
                data-testid="preview-unlock-button"
                onClick={unlock}
                disabled={unlocking || !online}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6"
              >
                {unlocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                Unlock & Download · {creditLabel(stage?.cost || 1)}
              </Button>
            </div>
          </footer>
        ) : (
          <footer data-testid="preview-downloaded-footer" className="px-5 py-4 border-t border-slate-200 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm font-medium text-emerald-700 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Downloaded to your device.
              </p>
              <ExpiryCountdown createdAt={downloadedDoc.created_at} />
            </div>
            <p data-testid="purge-notice-downloaded" className="text-xs text-slate-500 flex items-start gap-1.5">
              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600 mt-px" />
              {PURGE_NOTICE} You can also wipe it right now.
            </p>
            <div className="flex items-center justify-end gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="delete-from-server-button"
                    disabled={deleting}
                    variant="outline"
                    className="rounded-full px-5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Delete from server now
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Wipe this file from our servers now?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Make sure your download has finished first. This permanently deletes “{downloadedDoc.title}” from our servers and can’t be undone — the copy on your device is unaffected.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-wipe">Not yet</AlertDialogCancel>
                    <AlertDialogAction data-testid="confirm-wipe" onClick={deleteNow} className="bg-red-600 hover:bg-red-700">
                      Delete permanently
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button data-testid="preview-done-button" onClick={() => onUnlocked?.()} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6">
                <Download className="h-4 w-4 mr-2" /> Done
              </Button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
